import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { routesMap } from '../../../routes/routesMap';
import { useComponents } from '../../../api/hooks/useComponents';
import { useConductRiskSources } from '../../../api/hooks/useConductRiskSources';
import { useConductRiskValidation } from '../../../api/hooks/useConductRiskValidation';
import { downloadConductRiskSource } from '../../../api/functionApi';
import { driverNumberToSlug, driverNumberFromKind, isInvestigationDriver, isAnnexKind, INVESTIGATION_SUMMARY_SLUG } from '../../../utils/conductRiskDrivers';
import { DRIVER_METADATA } from './driverMetadata';
import DriverList from './DriverList';
import SourcesSection from './SourcesSection';
import ValidateDriversButton from './ValidateDriversButton';
import CreaReportModal from '../lista-report/CreaReportModal';
import { SOURCE_ID_TO_DOC_TYPE, SOURCES } from '../lista-report/sources';
import { useSourcesActions } from './hooks/useSourcesActions';
import type { ConductRiskSourceLink } from '../../../api/apiTypesConductRisk';
import type { DriverListItem } from './types';
import type { ReportStato } from '../../../api/apiTypes';

interface Props {
  reportId: string;
  reportName?: string;
}

const ContainerDettaglioReportPage = ({ reportId, reportName = 'Report' }: Props) => {
  const navigate = useNavigate();
  const { components, loading, error } = useComponents(reportId || undefined);
  const {
    status:               validationStatus,
    issuesByDocumentType,
    error:                validationError,
    refetch:              refetchValidation,
  } = useConductRiskValidation(reportId || undefined);
  const { links: sourceLinks, refetch: refetchSources } = useConductRiskSources(reportId || undefined);
  const [addSourcesOpen, setAddSourcesOpen] = useState(false);

  const {
    replacingDocType,
    handleReplaceFile,
    handleUnlink,
    handleAddSources,
  } = useSourcesActions({ reportId, sourceLinks, refetchSources, refetchValidation });

  // Set dei docType con missing_link — usato per abilitare "Carica Fonti"
  // nell'header e filtrare le card mostrate nel modal.
  const missingDocTypeSet = useMemo(() => {
    const set = new Set<string>();
    Object.entries(issuesByDocumentType).forEach(([dt, issues]) => {
      if (issues?.includes('missing_link')) set.add(dt);
    });
    return set;
  }, [issuesByDocumentType]);

  const visibleSourceIdsForAdd = useMemo<string[]>(() => {
    return SOURCES
      .filter(src => {
        if (src.multi) return true;
        const docType = SOURCE_ID_TO_DOC_TYPE[src.id];
        if (!docType) return true;
        return missingDocTypeSet.has(docType);
      })
      .map(src => src.id);
  }, [missingDocTypeSet]);

  const driverItems = useMemo<DriverListItem[]>(() => {
    return components
      .map(c => {
        const meta = DRIVER_METADATA[c.kind];
        if (!meta) return null;
        const driverNumber = driverNumberFromKind(c.kind);
        const isAnnex      = isAnnexKind(c.kind);
        if (driverNumber === null && !isAnnex) return null;
        return {
          kind:         c.kind,
          // Gli Annex non hanno un numero ordinale: uso un valore alto per
          // farli comparire in fondo alla sezione "Driver di analisi".
          driverNumber: driverNumber ?? 999,
          componentId:  c.id,
          name:         c.name,
          status:       c.status as ReportStato,
          description:  meta.description,
          sources:      meta.sources,
          icon:         meta.icon,
          category:     isAnnex
            ? 'annex'
            : isInvestigationDriver(driverNumber!) ? 'investigation' : 'analisi',
        } as DriverListItem;
      })
      .filter((d): d is DriverListItem => d !== null)
      .sort((a, b) => a.driverNumber - b.driverNumber);
  }, [components]);

  // Gli Annex vengono mostrati insieme ai Driver di analisi ma come card
  // di sola consultazione (non cliccabili) — vedi DriverList.
  const analisiItems       = useMemo(() => driverItems.filter(d => d.category === 'analisi' || d.category === 'annex'), [driverItems]);
  const investigationItems = useMemo(() => driverItems.filter(d => d.category === 'investigation'), [driverItems]);

  const handleDriverClick = (driver: DriverListItem) => {
    // Gli Annex sono di sola consultazione: non hanno pagina di dettaglio.
    if (driver.category === 'annex') return;
    navigate(routesMap.CONDUCT_RISK_DRIVER_DETAIL(reportName, driverNumberToSlug(driver.driverNumber)), {
      state: { id: reportId },
    });
  };

  const handleSummaryClick = () => {
    navigate(routesMap.CONDUCT_RISK_DRIVER_DETAIL(reportName, INVESTIGATION_SUMMARY_SLUG), {
      state: { id: reportId },
    });
  };

  const handleSourceDownload = async (
    link:  ConductRiskSourceLink | undefined,
    label: string,
  ) => {
    // Fonte non ancora caricata sul report: feedback informativo, niente 404.
    if (!link) {
      Swal.fire({
        icon:  'info',
        title: 'Fonte non caricata',
        html:  `<div style="max-width:500px;text-align:center;color:#374151;line-height:1.5">
                  <p>La fonte <strong>${label}</strong> non è ancora stata caricata su questo report.</p>
                  <p style="margin-top:8px;font-size:12px;color:#6B7280">
                    Caricala dalla sezione <em>Fonti del report</em> per rigenerare i driver.
                  </p>
                </div>`,
        confirmButtonColor: '#374151',
      });
      return;
    }
    try {
      const { blob, filename } = await downloadConductRiskSource(reportId, link.id);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      // Il BE espone Content-Disposition via CORS
      // (expose_headers=["Content-Disposition"]): usiamo direttamente il
      // filename sintetizzato con l'estensione corretta in base al mimetype.
      // Il fallback textuale copre solo casi di header malformato/assente.
      a.download = filename ?? link.source?.file_name ?? String(link.document_type);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      Swal.fire({
        icon:  'error',
        title: 'Download fallito',
        text:  err?.message ?? 'Impossibile scaricare il file.',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <ValidateDriversButton reportId={reportId} validationOk={validationStatus === 'OK'} />

      <DriverList
        analisi={analisiItems}
        investigation={investigationItems}
        sourceLinks={sourceLinks}
        onDriverClick={handleDriverClick}
        onSourceClick={handleSourceDownload}
        onSummaryClick={handleSummaryClick}
      />

      <SourcesSection
        validationStatus={validationStatus}
        issuesByDocumentType={issuesByDocumentType}
        sourceLinks={sourceLinks}
        replacingDocType={replacingDocType}
        validationError={validationError}
        hasMissingLinks={missingDocTypeSet.size > 0}
        onOpenAddSources={() => setAddSourcesOpen(true)}
        onReplace={handleReplaceFile}
        onUnlink={handleUnlink}
      />

      <CreaReportModal
        open={addSourcesOpen}
        mode="add-sources"
        visibleSourceIds={visibleSourceIdsForAdd}
        onClose={() => setAddSourcesOpen(false)}
        onConfirm={(data) => handleAddSources({ files: data.files })}
      />
    </div>
  );
};

export default ContainerDettaglioReportPage;
